// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Landbridge — tiny DOM helpers.

/** Escape a string for safe interpolation into innerHTML. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** A button armed by `armConfirm`. */
export interface Confirm {
  /** Put the button back to its idle label. */
  disarm: () => void;
  /**
   * Disarm only when `token` differs (by identity) from the last one seen. A
   * screen that redraws on a timer must use THIS, never bare `disarm()`: a net
   * round redraws every 250ms, so disarming per redraw wiped the armed label
   * within ~200ms and no second tap could ever land. Pass something that is
   * replaced only when the player actually did something — the game state is, on
   * every play/undo/hint/give-up/timeout — and an idle redraw leaves it armed.
   */
  disarmOn: (token: unknown) => void;
  armed: () => boolean;
}

/**
 * Turn a button into a two-tap confirm, for an action that cannot be undone. The
 * first tap only swaps the label; the second, within `timeoutMs`, runs it. A
 * stray tap costs a tap, not the round.
 */
export function armConfirm(
  btn: HTMLButtonElement,
  opts: { idle: string; armed: string; onConfirm: () => void; timeoutMs?: number },
): Confirm {
  const timeoutMs = opts.timeoutMs ?? 4000;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let armed = false;

  function paint(): void {
    btn.textContent = armed ? opts.armed : opts.idle;
    btn.classList.toggle('arming', armed);
  }

  function disarm(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (!armed) return;
    armed = false;
    paint();
  }

  // A sentinel no caller can hold, so the first disarmOn() always counts as a change.
  let lastToken: unknown = Symbol('unset');
  function disarmOn(token: unknown): void {
    if (token === lastToken) return;
    lastToken = token;
    disarm();
  }

  btn.addEventListener('click', () => {
    if (btn.disabled) return;
    if (armed) {
      disarm();
      opts.onConfirm();
      return;
    }
    armed = true;
    paint();
    timer = setTimeout(disarm, timeoutMs);
  });

  paint();
  return { disarm, disarmOn, armed: () => armed };
}
